import { useEffect, useState } from "react";
import { Plus, Trash2, BellRing, Loader2, CalendarPlus, Calendar, Pencil, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, downloadFile, currentLocale } from "@/lib/api";
import Modal from "@/components/Modal";

const FORM_VIDE = { client_id: "", chantier_id: "", titre: "", date: "", heure: "08:30", duree_minutes: 60 };
const pad = (n) => String(n).padStart(2, "0");

export default function Agenda() {
  const { t } = useTranslation();
  const [rdvs, setRdvs] = useState([]);
  const [clients, setClients] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(FORM_VIDE);
  const [busy, setBusy] = useState(false);
  const [calendlyLien, setCalendlyLien] = useState("");

  const load = () => api.get("/rendezvous").then((r) => setRdvs(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/calendly/statut")
      .then((r) => { if (r.data.connecte && r.data.lien) setCalendlyLien(r.data.lien); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (form.client_id) api.get(`/chantiers?client_id=${form.client_id}`).then((r) => setChantiers(r.data));
  }, [form.client_id]);

  function fermerModal() {
    setAddOpen(false);
    setEditId(null);
    setForm(FORM_VIDE);
  }

  function ouvrirModif(r) {
    const d = new Date(r.date_debut);
    setForm({
      client_id: r.client_id || "", chantier_id: r.chantier_id || "", titre: r.titre || "",
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      heure: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
      duree_minutes: r.duree_minutes || 60,
    });
    setEditId(r.id);
    setAddOpen(true);
  }

  async function submitRdv() {
    if (!form.titre.trim() || !form.date || (!editId && !form.client_id)) return toast.error(t("agenda.requis"));
    setBusy(true);
    try {
      const payload = {
        chantier_id: form.chantier_id || null,
        titre: form.titre, date_debut: new Date(`${form.date}T${form.heure}`).toISOString(),
        duree_minutes: Number(form.duree_minutes) || 60,
      };
      if (editId) {
        await api.put(`/rendezvous/${editId}`, payload);
        toast.success(t("agenda.modifie"));
      } else {
        await api.post("/rendezvous", { client_id: form.client_id, ...payload });
        toast.success(t("agenda.planifie"));
      }
      fermerModal();
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function supprimer(rdvId) {
    await api.delete(`/rendezvous/${rdvId}`).catch(() => {});
    toast.success(t("agenda.supprime"));
    load();
  }

  async function copierLienCalendly() {
    try {
      await navigator.clipboard.writeText(calendlyLien);
      toast.success(t("agenda.lien_copie"));
    } catch {
      toast.error(t("agenda.lien_copie_erreur"));
    }
  }

  const aVenir = rdvs.filter((r) => new Date(r.date_debut) >= new Date(Date.now() - 86400000)
    && r.calendly_statut !== "canceled");

  return (
    <div className="space-y-5 max-w-3xl" data-testid="page-agenda">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("agenda.titre")}</h1>
        <div className="flex items-center gap-2">
          {calendlyLien && (
            <button onClick={copierLienCalendly} title={calendlyLien} className="btn-secondary"
                    data-testid="btn-copier-lien-calendly">
              <Link2 size={18} /> {t("agenda.copier_lien_calendly")}
            </button>
          )}
          <button onClick={() => { setEditId(null); setForm(FORM_VIDE); setAddOpen(true); }} className="btn-accent" data-testid="btn-nouveau-rdv">
            <Plus size={20} /> {t("agenda.nouveau")}
          </button>
        </div>
      </div>

      <div className="panel !p-0 overflow-hidden">
        {aVenir.length === 0 ? (
          <p className="text-stone-500 text-sm py-10 text-center">{t("agenda.vide")}</p>
        ) : (
          <div className="divide-y divide-[#E6DFD1]" data-testid="liste-rdvs">
            {aVenir.map((r) => (
              <div key={r.id} className="flex items-center gap-4 p-4" data-testid={`row-rdv-${r.id}`}>
                <div className="w-14 h-14 rounded-lg bg-[#12100D] text-[#D9A461] flex flex-col items-center justify-center shrink-0">
                  <span className="font-serif font-bold text-lg leading-none">{new Date(r.date_debut).getDate()}</span>
                  <span className="text-[10px] uppercase">{new Date(r.date_debut).toLocaleDateString(currentLocale(), { month: "short" })}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{r.titre}</div>
                  <div className="text-xs text-stone-500">
                    {[r.client_nom, new Date(r.date_debut).toLocaleTimeString(currentLocale(), { hour: "2-digit", minute: "2-digit" }), `${r.duree_minutes} ${t("commun.min")}`].filter(Boolean).join(" · ")}
                  </div>
                  {r.source === "calendly" && (
                    <span className="text-[11px] font-semibold text-[#8A5A2B] inline-flex items-center gap-1 mt-0.5"
                          data-testid={`badge-calendly-${r.id}`}>
                      <Calendar size={11} /> {t("reglages.calendly_badge")}
                    </span>
                  )}
                  {r.rappel_envoye && (
                    <span className="text-[11px] font-semibold text-[#276440] inline-flex items-center gap-1 mt-0.5">
                      <BellRing size={11} /> {t("agenda.rappel_envoye")}
                    </span>
                  )}
                </div>
                {r.source !== "calendly" && (
                  <>
                <button onClick={() => ouvrirModif(r)}
                        title={t("agenda.modifier")}
                        className="w-11 h-11 flex items-center justify-center rounded-lg bg-white border border-[#E6DFD1] text-[#8A5A2B] shrink-0"
                        data-testid={`btn-modifier-rdv-${r.id}`}>
                  <Pencil size={17} />
                </button>
                <button onClick={() => downloadFile(`/rendezvous/${r.id}/ics`, `rdv-${r.id}.ics`)}
                        title={t("agenda.ics_title")}
                        className="w-11 h-11 flex items-center justify-center rounded-lg bg-white border border-[#E6DFD1] text-[#8A5A2B] shrink-0"
                        data-testid={`btn-ics-rdv-${r.id}`}>
                  <CalendarPlus size={17} />
                </button>
                <button onClick={() => supprimer(r.id)}
                        className="w-11 h-11 flex items-center justify-center rounded-lg bg-[#FBE4E1] text-[#A32E20] shrink-0"
                        data-testid={`btn-supprimer-rdv-${r.id}`}>
                  <Trash2 size={17} />
                </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal open={addOpen} onClose={fermerModal} title={editId ? t("agenda.modal_modif_titre") : t("agenda.modal_titre")} testid="modal-nouveau-rdv">
        <div className="space-y-4">
          <select className="input-field" value={form.client_id} disabled={!!editId}
                  onChange={(e) => setForm({ ...form, client_id: e.target.value, chantier_id: "" })}
                  data-testid="select-rdv-client">
            <option value="">{t("agenda.choisir_client")}</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
          {chantiers.length > 0 && (
            <select className="input-field" value={form.chantier_id}
                    onChange={(e) => setForm({ ...form, chantier_id: e.target.value })} data-testid="select-rdv-chantier">
              <option value="">{t("agenda.chantier")}</option>
              {chantiers.map((c) => (
                <option key={c.id} value={c.id}>{c.titre}</option>
              ))}
            </select>
          )}
          <input className="input-field" placeholder={t("agenda.objet")} value={form.titre}
                 onChange={(e) => setForm({ ...form, titre: e.target.value })} data-testid="input-rdv-titre" />
          <div className="grid grid-cols-2 gap-3">
            <input className="input-field" type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="input-rdv-date" />
            <input className="input-field" type="time" value={form.heure}
                   onChange={(e) => setForm({ ...form, heure: e.target.value })} data-testid="input-rdv-heure" />
          </div>
          <select className="input-field" value={form.duree_minutes}
                  onChange={(e) => setForm({ ...form, duree_minutes: e.target.value })} data-testid="select-rdv-duree">
            <option value={30}>{t("agenda.duree_30")}</option>
            <option value={60}>{t("agenda.duree_60")}</option>
            <option value={120}>{t("agenda.duree_120")}</option>
            <option value={240}>{t("agenda.duree_240")}</option>
            <option value={480}>{t("agenda.duree_480")}</option>
          </select>
          <button onClick={submitRdv} disabled={busy} className="btn-accent w-full" data-testid="btn-confirmer-rdv">
            {busy ? <Loader2 className="animate-spin" size={18} /> : (editId ? t("agenda.enregistrer") : t("agenda.planifier"))}
          </button>
        </div>
      </Modal>
    </div>
  );
}
